import { ReactNode } from 'react';
import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow, { WorkflowAction, WorkflowGroupOptions } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { ActionKindMeta } from './actionConfig';
import { ACTION_STATUS_LABELS, actionStatusDisplay } from './StatusBadge';
import StatusIcon, { indicatorColor, statusIndicator } from './StatusIcon';

// The generic panel for a fanned-out (per-building) step type (OLIO-2611): one row per
// building, with the step type's own detail plugged in via renderRow. Deliberately
// minimal — kind-specific mechanics (Facility Decisions' search/select-all/bulk) live
// in that kind's own panel, not here.
interface Props {
  actions: WorkflowAction[];
  renderRow: (action: WorkflowAction) => ReactNode;
  // The explanatory line above the rows (the step's API description) — where Facility
  // Decisions puts its search toolbar, per the Figma.
  blurb?: string | null;
  // Right-aligned content on the row's header line (e.g. the assessment View link).
  aside?: (action: WorkflowAction) => ReactNode;
}

// The building label for a fanned instance — read straight off the action's own group. A
// fanned action always carries its group (the API serves the name whenever a group is present);
// the readable placeholder only guards the nullable type for a group-less action, so a stray one
// renders legibly instead of blank (and doesn't sort to the top on an empty string).
export function buildingName(action: WorkflowAction): string {
  return action.group?.name ?? 'Unknown building';
}

// Every per-building list reads alpha by building name — one comparator so the panels,
// the outcome rows, and the winner dropdown can never disagree on order.
export function sortedByBuildingName(actions: WorkflowAction[]): WorkflowAction[] {
  return [...actions].sort((a, b) => buildingName(a).localeCompare(buildingName(b)));
}

// The pluralized type noun for a set of fanned buildings — the group type's display name (e.g.
// "SNFs") only when every building agrees, else `fallback`. One rule so the header caption and
// the body heading can't disagree on a mixed-type set. `fallback` carries the caller's casing
// ("facilities" inline vs "Facilities" as a heading).
export function buildingTypeNoun(actions: WorkflowAction[], fallback: string): string {
  const typeNames = new Set(actions.map((action) => action.group?.groupType?.displayName));
  const [only] = [...typeNames];
  return typeNames.size === 1 && only ? `${only}s` : fallback;
}

export function sortedGroupsByName(groups: WorkflowGroupOptions[]): WorkflowGroupOptions[] {
  return [...groups].sort((a, b) => a.name.localeCompare(b.name));
}

// "Updated by <name> on <date>" — the designed attribution line on decided rows and
// outcome rows; null until the action completes.
export function completionAttribution(action: WorkflowAction): string | null {
  if (!action.completedAt) return null;
  const by = action.completedByName ? ` by ${action.completedByName}` : '';
  return `Updated${by} on ${simpleDateAtTime(action.completedAt)}`;
}

// The header rollup for a fanned kind — "1 Pending • 2 Accepted • 1 Denied". Completed
// rows count under their recorded result (meta.countLabels); everything else under its
// status label. Reading order comes from meta.countLabelOrder, never from the data.
export function fanoutCounts(workflow: Workflow, actions: WorkflowAction[], meta: ActionKindMeta | undefined): string {
  const counts = new Map<string, number>();
  actions.forEach((action) => {
    const result = meta?.resultValueName ? workflow.valueFor(meta.resultValueName, action)?.value : undefined;
    // An undecided-but-actionable row rolls up as "Pending" (matching its "Pending
    // Decision" subtitle) — "Ready for Review" is the card badge's vocabulary, not the
    // building counts'.
    const label =
      (action.isCompleted && typeof result === 'string' && meta?.countLabels?.[result]) ||
      (action.isActionRequired ? 'Pending' : actionStatusDisplay(action.effectiveStatus).label);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  // Emit in the kind's DECLARED order, not the order the buildings happened to arrive in.
  // A Map iterates by insertion, so the caption read "1 Denied • 2 Accepted" one poll and
  // "2 Accepted • 1 Denied" the next as the API returned the same buildings in a different
  // order — the flip in OLIO-3135. A label the kind doesn't declare is dropped from the
  // caption (its row below still shows it); a label with no buildings was never counted, so
  // an empty category is absent rather than "0 Denied".
  const declared = meta?.countLabelOrder?.filter((label) => counts.has(label)) ?? [];
  // … unless dropping the undeclared ones would empty the caption outright — every building
  // canceled with the referral, say. CategoryCard renders the subtitle behind a truthiness
  // check, so "" takes the line off the card entirely, and a card that silently loses its
  // caption on a canceled referral is a worse answer than naming the state the buildings are
  // actually in.
  const observed = Array.from(counts.keys());
  // The fallback is ordered too. Handing it raw Map keys would reinstate the very flip this
  // function exists to stop — just among the undeclared labels, for a referral holding two of
  // them at once (some buildings Error, others Skipped). Status labels lead in lifecycle order;
  // anything else (a kind's own result labels) follows in observed order.
  const fallback = [
    ...ACTION_STATUS_LABELS.filter((label) => counts.has(label)),
    ...observed.filter((label) => !ACTION_STATUS_LABELS.includes(label)),
  ];
  const order = declared.length ? declared : fallback;
  return order.map((label) => `${counts.get(label)} ${label}`).join(' • ');
}

export default function FanoutPanel({ actions, renderRow, blurb, aside }: Props) {
  const ordered = sortedByBuildingName(actions);

  return (
    <Panel data-testid='fanout-panel'>
      {blurb && <Blurb data-testid='fanout-blurb'>{blurb}</Blurb>}
      {ordered.map((action) => (
        <Row key={action.id} data-testid='fanout-row' data-group-id={action.groupId ?? undefined}>
          <StatusIcon indicator={statusIndicator(action)} size={16} />
          <RowMain>
            <RowHeader>
              <RowName>{buildingName(action)}</RowName>
              <RowStatus $color={indicatorColor(statusIndicator(action))}>
                {actionStatusDisplay(action.effectiveStatus).label}
              </RowStatus>
              {aside && <RowAside>{aside(action)}</RowAside>}
            </RowHeader>
            {renderRow(action)}
          </RowMain>
        </Row>
      ))}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Blurb = styled(BodySmall)`
  padding: 12px 16px;
  color: ${colors.black75};
  border-bottom: 1px solid ${colors.black5};
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }

  > input {
    margin-top: 3px;
  }

  > span {
    margin-top: 2px;
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const RowAside = styled.span`
  margin-left: auto;
`;

const RowName = styled(BodySmallBold)`
  font-size: 14px;
`;

const RowStatus = styled(BodySmall)<{ $color?: string }>`
  color: ${({ $color }) => $color ?? colors.black50};
`;
